import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useOrganizationStore } from '@/stores/organization'

// The region whose spelling of a currency is unmarked (no « CA » / « US »
// prefix on the symbol). Anything not listed falls back to Canada, the only
// market this app is sold in.
const CURRENCY_REGION: Record<string, string> = { CAD: 'CA', USD: 'US' }

export function useFormatters() {
  const orgStore = useOrganizationStore()
  const { locale: uiLocale } = useI18n()

  const currency = computed(() => orgStore.currentOrganization?.attributes.currency || 'CAD')

  // Format against the active UI language (vue-i18n) so dates/money follow the
  // language the user is actually reading, not the organization's stored locale.
  // ⚠ REGION-QUALIFIED, and the region follows the money. A bare `fr` is France
  // to Intl, and France writes a Canadian dollar as « 110,38 $CA » — which is
  // what every amount on the desk read. Québec writes « 110,38 $ », and only
  // `fr-CA` knows that; `en-CA` likewise prints « $110.38 » where `en` (US)
  // prints « CA$110.38 ». The currency's home region is the one whose spelling
  // of it is unmarked.
  const region = computed(() => CURRENCY_REGION[currency.value] ?? 'CA')
  const locale = computed(() => `${uiLocale.value}-${region.value}`)

  // Date-only strings go through toLocalDate: `new Date('2026-07-16')` parses
  // as UTC midnight, which renders as the PREVIOUS day everywhere west of UTC.
  const formatDate = (date: string | Date | null | undefined, options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }) => {
    if (!date) return ''
    return new Intl.DateTimeFormat(locale.value, options).format(toLocalDate(date))
  }

  const formatDateTime = (date: string | Date | null | undefined) => {
    if (!date) return ''
    return new Intl.DateTimeFormat(locale.value, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(date))
  }

  // « 10 oct., 10 h 36 » — the year only when it is not this one. A diary
  // stamp in a narrow column wraps on the year it does not need.
  const formatDateTimeShort = (date: string | Date | null | undefined) => {
    if (!date) return ''
    const d = new Date(date)
    if (d.getFullYear() !== new Date().getFullYear()) return formatDateTime(d)
    return new Intl.DateTimeFormat(locale.value, {
      day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit'
    }).format(d)
  }

  const formatTime = (date: string | Date | null | undefined) => {
    if (!date) return ''
    return new Intl.DateTimeFormat(locale.value, {
      timeStyle: 'short'
    }).format(new Date(date))
  }

  // Parse a date-only `YYYY-MM-DD` string as LOCAL midnight (native `new Date`
  // treats it as UTC, which shifts a day in negative offsets); pass other
  // strings/Dates through unchanged.
  const toLocalDate = (v: string | Date): Date => {
    if (v instanceof Date) return v
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v)
  }

  // A compact, locale-aware date range for re-treatment windows (e.g. "Aug 4 – 8").
  // `formatRange` collapses a same-day range and omits a repeated month/year.
  //
  // ⚠ The YEAR appears as soon as either end leaves the current one. Without it
  // a window that lapsed LAST year printed « 25 sept. – 10 oct. » beside the
  // word « En retard », and the two read as a contradiction — an upcoming date
  // called late. The predicate was right; the label was hiding the only fact
  // that explained it.
  const formatDateRange = (from: string | Date | null | undefined, to: string | Date | null | undefined) => {
    if (!from || !to) return ''
    const start = toLocalDate(from)
    const end = toLocalDate(to)
    const thisYear = new Date().getFullYear()
    const spansAnotherYear = start.getFullYear() !== thisYear || end.getFullYear() !== thisYear
    return new Intl.DateTimeFormat(locale.value, {
      month: 'short',
      day: 'numeric',
      ...(spansAnotherYear ? { year: 'numeric' } : {}),
    }).formatRange(start, end)
  }

  // A date for DENSE rows: « 16 sept. », and the year only when it is not this
  // one. Same rule formatDateRange already applies — a year printed on every
  // row of a list about next week is four characters of noise on every row.
  const formatDateCompact = (date: string | Date | null | undefined) => {
    if (!date) return ''
    const d = toLocalDate(date)
    return new Intl.DateTimeFormat(locale.value, {
      month: 'short',
      day: 'numeric',
      ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    }).format(d)
  }

  const formatCurrency = (value: number | string | undefined) => {
    const num = typeof value === 'string' ? parseFloat(value) : (value || 0)
    return new Intl.NumberFormat(locale.value, {
      style: 'currency',
      currency: currency.value
    }).format(num)
  }

  // Compact money for tight surfaces (the board's capacity bar): no cents, a
  // narrow « $ » — « 4 040 $ » instead of « 4 040,00 $CA », ~half the width, so
  // two of them fit side by side in a narrow cell without truncation.
  const formatCurrencyCompact = (value: number | string | undefined) => {
    const num = typeof value === 'string' ? parseFloat(value) : (value || 0)
    return new Intl.NumberFormat(locale.value, {
      style: 'currency',
      currency: currency.value,
      maximumFractionDigits: 0,
      currencyDisplay: 'narrowSymbol',
    }).format(num)
  }

  // ⭐ THE SAME NUMBER WITHOUT ITS SYMBOL — for the NUMERATOR of a pair whose
  // denominator already carries the unit: « 3 840 / 3 800 $ », never
  // « 3 840 $ / 3 800 $ ». Grouping and rounding stay identical to
  // formatCurrencyCompact so the two halves cannot read as different scales.
  const formatNumberCompact = (value: number | string | undefined) => {
    const num = typeof value === 'string' ? parseFloat(value) : (value || 0)
    return new Intl.NumberFormat(locale.value, { maximumFractionDigits: 0 }).format(num)
  }

  return {
    formatDate,
    formatDateTime,
    formatDateTimeShort,
    formatTime,
    formatDateRange,
    formatDateCompact,
    formatCurrency,
    formatCurrencyCompact,
    formatNumberCompact
  }
}
